import type { Translations } from './ru'

export const enTranslations: Translations = {
  langSwitcher: {
    aria: 'Switch language',
    ru: 'Русский',
    en: 'English',
  },

  nav: {
    allTools: 'All tools',
    blog: 'Blog',
  },

  badge: {
    local: 'In browser',
    server: 'On server',
    localFirst: 'In browser*',
    localTitle: 'Text is processed in your browser and never sent to a server',
    serverTitle: 'Text is sent to a server for processing',
    localFirstTitle:
      'Processed in your browser by default. A server option is available when needed.',
  },

  common: {
    textTools: 'Tools',
    goToTool: 'Open',
    scenarios: 'Use cases',
    howTo: 'How to use it',
    faq: 'Questions',
    otherTools: 'Other tools',
    allTools: 'All tools',
    relatedTools: 'Related tools',
    updated: 'Updated',
    allBlogPosts: 'All blog posts',
  },

  hero: {
    title: 'Online developer tools',
    description:
      'JSON, YAML, Base64, UUID, cron and more right in your browser. No sign-up and no server upload.',
  },

  catalog: {
    title: 'Tool catalog',
    allJobs: 'All jobs',
    searchLabel: 'Find a tool',
    searchPlaceholder: 'Find JSON, cron, Base64...',
    enterHint: 'Enter to open',
    frequent: 'Frequently used',
    resultCount: 'Found: {count}',
    reset: 'Reset',
    noMatchTitle: 'No tools found',
    noMatchDescription: 'Try another query or reset the job filter.',
  },

  notFound: {
    eyebrow: '404: page not found',
    title: 'This devdeck page does not exist',
    description:
      'The tool may have moved, the link may be outdated, or the address may contain a typo. Return to the catalog and open an available utility.',
    primaryAction: 'Home',
    secondaryAction: 'Tool catalog',
    fallbackTitle: 'Available tools',
  },

  categories: {
    count: 'Count',
    clean: 'Clean',
    format: 'Format',
    encode: 'Encode',
    generate: 'Generate',
    test: 'Test',
    seo: 'SEO and social',
  },

  tools: {
    textDiff: {
      title: 'Compare text online',
      shortDescription: 'Line-by-line text comparison with highlighted changes.',
      longDescription:
        'A local text diff for code, configs, logs and payloads: compare lines and changed fragments directly in your browser.',
      seoTitle: 'Text diff online — compare text | devdeck.ru',
      seoDescription:
        'Compare two texts online with line numbers and highlighted additions and removals. Ignore case and edge whitespace with no server upload.',
      useCases: ['Code and configs', 'Logs', 'API payloads', 'Documentation drafts'],
      howTo: [
        'Paste the original and modified text into the two fields.',
        'Optionally ignore case or leading and trailing whitespace on each line.',
        'Click Compare and inspect the unified diff with line numbers.',
        'Copy the readable unified diff or swap the texts.',
      ],
      faq: [
        {
          question: 'Are the texts sent to a server?',
          answer: 'No. Comparison runs entirely in your browser, so field contents stay on your device.',
        },
        {
          question: 'Which whitespace can be ignored?',
          answer: 'The option ignores leading and trailing whitespace on each line only. Whitespace inside a line remains meaningful.',
        },
        {
          question: 'Is there a size limit?',
          answer: 'Yes. To keep the page responsive, each side accepts up to 500 KB and 5,000 lines.',
        },
      ],
    },
    jsonFormatter: {
      title: 'JSON formatter',
      shortDescription: 'Format, minify and validate JSON.',
      longDescription: 'Readable JSON for API responses, logs and configs right in the browser.',
      seoTitle: 'JSON formatter online — devdeck.ru',
      seoDescription: 'Format, minify and validate JSON locally in your browser.',
      useCases: ['API responses', 'Logs', 'Configs', 'Minification'],
      howTo: [
        'Paste JSON into the editor.',
        'Choose Format, Minify or Validate. The output updates after a short pause.',
        'Copy the result back.',
      ],
      faq: [
        {
          question: 'Is the data sent to a server?',
          answer: 'No. JSON is processed in the browser. Your data stays on your device.',
        },
        {
          question: 'Will large JSON freeze the page?',
          answer: 'Processing is synchronous. If your JSON is very large, the browser may freeze briefly. For production workloads, use dedicated tools.',
        },
      ],
    },
    jsonMinifier: {
      title: 'JSON minifier',
      shortDescription: 'Minify JSON into one compact string.',
      longDescription:
        'Compact JSON for payloads, configs and network transfer with local syntax validation.',
      seoTitle: 'JSON minifier online — compress JSON locally | devdeck.ru',
      seoDescription:
        'Minify JSON online in your browser: remove whitespace and line breaks, validate syntax and copy the compact result. Free with no server upload.',
      useCases: ['API payloads', 'Compact configs', 'Webhook bodies', 'Network transfer'],
      howTo: [
        'Paste JSON into the editor.',
        'The compact result updates after a short pause and removes extra whitespace and line breaks.',
        'Check parser errors for invalid JSON or copy the compact result.',
      ],
      faq: [
        {
          question: 'Is JSON sent to a server?',
          answer: 'No. Minification runs with JSON.parse and JSON.stringify in your browser.',
        },
        {
          question: 'Does minification change the data?',
          answer:
            'No. The tool removes formatting only. Key order follows the standard JavaScript parser output.',
        },
        {
          question: 'How is this different from JSON formatter?',
          answer:
            'JSON formatter makes a document readable with indentation, while JSON minifier creates a compact version for transfer, configs or payloads.',
        },
      ],
    },
    yamlFormatter: {
      title: 'YAML formatter',
      shortDescription: 'Format and validate YAML locally.',
      longDescription:
        'Readable YAML for configs, CI/CD, Kubernetes manifests and documentation right in the browser.',
      seoTitle: 'YAML formatter online — devdeck.ru',
      seoDescription:
        'Format and validate YAML locally in your browser: useful parser errors, copy and clear with no server upload.',
      useCases: ['Configs', 'CI/CD', 'Kubernetes YAML', 'Documentation'],
      howTo: [
        'Paste YAML into the editor.',
        'Click Format to normalize indentation or Validate to see syntax errors.',
        'Copy the result or clear the fields.',
      ],
      faq: [
        {
          question: 'Is YAML sent to a server?',
          answer:
            'No. Formatting and validation happen in your browser, so the data stays on your device.',
        },
        {
          question: 'Does it show line numbers for errors?',
          answer:
            'Yes. When the parser returns a location, the tool shows the line and column next to the error message.',
        },
        {
          question: 'Are multi-document YAML streams supported?',
          answer:
            'Yes. A YAML stream separated with --- is parsed locally and formatted as multiple documents.',
        },
      ],
    },
    htmlFormatter: {
      title: 'HTML formatter',
      shortDescription: 'Format and minify HTML locally.',
      longDescription:
        'An HTML formatter/minifier for markup, email templates and page snippets: formatting, minification and useful parser errors right in the browser.',
      seoTitle: 'HTML formatter online — devdeck.ru',
      seoDescription:
        'Format and minify HTML locally in your browser: indentation, minify, copy, clear and useful errors with no server upload.',
      useCases: ['HTML snippets', 'Email templates', 'Page markup', 'Minification'],
      howTo: [
        'Paste HTML into the editor.',
        'Click Format for readable indentation or Minify for compact output.',
        'If the parser cannot read the markup, fix the issue shown in the result area.',
        'Copy the result or clear the fields.',
      ],
      faq: [
        {
          question: 'Is HTML sent to a server?',
          answer:
            'No. Formatting and minification run in your browser, so the markup stays on your device.',
        },
        {
          question: 'What happens with broken HTML?',
          answer:
            'The tool parses HTML locally. If the syntax cannot be processed, it shows a clear error instead of returning a misleading result.',
        },
        {
          question: 'Does minification change script or pre content?',
          answer:
            'No. The minifier preserves script, style, pre and textarea blocks so meaningful whitespace and code are not broken.',
        },
      ],
    },
    javascriptFormatter: {
      title: 'JavaScript formatter',
      shortDescription: 'Format and minify JavaScript locally.',
      longDescription:
        'A JavaScript formatter/minifier for snippets, code fragments and client-side bundle drafts: Prettier formatting, Terser minify and useful syntax errors without running code.',
      seoTitle: 'JavaScript formatter online — devdeck.ru',
      seoDescription:
        'Format and minify JavaScript locally in your browser: Prettier, Terser, copy, clear and clear syntax errors with no server upload.',
      useCases: ['JavaScript snippets', 'Code review', 'Bundle drafts', 'Minification'],
      howTo: [
        'Paste JavaScript into the editor.',
        'Click Format for readable indentation or Minify for compact output.',
        'If the parser cannot read the code, fix the issue shown in the result area.',
        'Copy the result or clear the fields.',
      ],
      faq: [
        {
          question: 'Is JavaScript sent to a server?',
          answer:
            'No. Formatting and minification run in your browser, so source code stays on your device.',
        },
        {
          question: 'Does the tool execute pasted code?',
          answer:
            'No. The code is only parsed and printed back or minified. The tool does not use eval, Function or script execution.',
        },
        {
          question: 'Which libraries are used?',
          answer:
            'Formatting uses Prettier standalone with Babel/ESTree plugins, and minification uses Terser. Both libraries have permissive OSS licenses and load only on this tool page.',
        },
      ],
    },
    cssFormatter: {
      title: 'CSS formatter',
      shortDescription: 'Format and minify CSS locally.',
      longDescription:
        'A CSS formatter/minifier for stylesheet fragments, component styles and theme tokens: Prettier formatting, minify, copy, clear and useful syntax errors right in the browser.',
      seoTitle: 'CSS formatter online — devdeck.ru',
      seoDescription:
        'Format and minify CSS locally in your browser: Prettier parser, minify, copy, clear and useful syntax errors with no server upload.',
      useCases: ['CSS snippets', 'Stylesheets', 'Theme tokens', 'Minification'],
      howTo: [
        'Paste CSS into the editor.',
        'Click Format for readable indentation or Minify for compact output.',
        'If the parser cannot read the stylesheet, fix the issue shown in the result area.',
        'Copy the result or clear the fields.',
      ],
      faq: [
        {
          question: 'Is CSS sent to a server?',
          answer:
            'No. Formatting and minification run in your browser, so the stylesheet stays on your device.',
        },
        {
          question: 'Does it show syntax errors?',
          answer:
            'Yes. The tool uses Prettier\'s CSS parser and shows the line, column and parser message when a location is available.',
        },
        {
          question: 'Can minification change CSS meaning?',
          answer:
            'Minification removes comments and extra whitespace after a successful parse. Still run production styles through your own pipeline when it handles autoprefixing or advanced optimizations.',
        },
      ],
    },
    sqlFormatter: {
      title: 'SQL formatter',
      shortDescription: 'Format SQL queries locally with a dialect selector.',
      longDescription:
        'A local SQL formatter for SELECT, JOIN, CTE, INSERT and other queries: readable indentation and uppercase keywords with no server upload.',
      seoTitle: 'SQL formatter online — format SQL queries | devdeck.ru',
      seoDescription:
        'SQL formatter online: make queries readable with indentation and uppercase keywords. PostgreSQL, MySQL, SQLite and other dialects, processed locally in your browser.',
      useCases: ['SQL queries', 'JOINs and CTEs', 'Migrations', 'Code review'],
      howTo: [
        'Paste a SQL query into the editor.',
        'Choose a dialect if you need the parser to follow PostgreSQL, MySQL, SQLite or another syntax more closely.',
        'Click Format to get readable indentation and uppercase keywords.',
        'Copy the result or clear the fields.',
      ],
      faq: [
        {
          question: 'Is SQL sent to a server?',
          answer:
            'No. Formatting runs in your browser, so the source query and result are not sent to a server.',
        },
        {
          question: 'Does the dialect selector validate SQL for a specific database?',
          answer:
            'No. The dialect helps the formatter parse common syntax differences, but it is not a replacement for your database parser or semantic validation.',
        },
        {
          question: 'Can the tool change query meaning?',
          answer:
            'The formatter changes whitespace, line breaks and keyword case. Always test production queries in your own environment before applying them.',
        },
      ],
    },
    xmlFormatter: {
      title: 'XML formatter',
      shortDescription: 'Format, minify and validate XML locally.',
      longDescription:
        'An XML formatter/validator for payloads, SOAP, RSS, sitemaps and configs: indentation, minify, copy, clear and parser errors right in the browser.',
      seoTitle: 'XML formatter online — devdeck.ru',
      seoDescription:
        'Format, minify and validate XML locally in your browser: indentation, minify, copy, clear and syntax errors with no server upload.',
      useCases: ['API payloads', 'SOAP/XML responses', 'RSS and sitemaps', 'Configs'],
      howTo: [
        'Paste XML into the editor.',
        'Click Format for readable indentation, Minify for a compact string, or Validate to check syntax.',
        'If the XML is invalid, fix the parser error. Line and position are shown when the browser returns them.',
        'Copy the result or clear the fields.',
      ],
      faq: [
        {
          question: 'Is XML sent to a server?',
          answer:
            'No. Formatting, minification and validation run in your browser, so XML stays on your device.',
        },
        {
          question: 'Does it show the error line and column?',
          answer:
            'Yes, when the browser XML parser returns a location. Otherwise the original parser message is shown.',
        },
        {
          question: 'Does minification change text inside tags?',
          answer:
            'Minification removes whitespace between tags, but it does not collapse text nodes inside elements.',
        },
      ],
    },
    csvToJson: {
      title: 'CSV to JSON',
      shortDescription: 'Convert CSV to JSON with delimiter detection.',
      longDescription:
        'A local CSV to JSON converter for exports, tables and test data: paste CSV or upload a small file, choose header handling and get JSON.',
      seoTitle: 'CSV to JSON online — devdeck.ru',
      seoDescription:
        'Convert CSV to JSON locally in your browser: paste text, upload a CSV file, detect delimiters, toggle header row mode, copy and download.',
      useCases: ['Table exports', 'API payloads', 'Test data', 'ETL drafts'],
      howTo: [
        'Paste CSV into the editor or upload a small .csv file.',
        'Keep delimiter detection on or choose comma, semicolon, tab or pipe manually.',
        'Enable header row mode if the first row contains field names.',
        'Click Convert, then copy or download the JSON.',
      ],
      faq: [
        {
          question: 'Is CSV sent to a server?',
          answer:
            'No. Conversion runs in your browser, so the source CSV and result are not sent to a server.',
        },
        {
          question: 'Which delimiters are supported?',
          answer:
            'Comma, semicolon, tab and pipe are supported. You can keep automatic detection or choose a delimiter manually.',
        },
        {
          question: 'Are quoted values supported?',
          answer:
            'Yes. The tool handles quoted fields, CRLF line breaks and escaped double quotes inside values using "".',
        },
      ],
    },
    jsonToCsv: {
      title: 'JSON to CSV',
      shortDescription: 'Convert a JSON array or object to CSV with flattened nested fields.',
      longDescription:
        'A local JSON to CSV converter for API responses, exports and test data: paste JSON, get header-based CSV and download the result.',
      seoTitle: 'JSON to CSV online — devdeck.ru',
      seoDescription:
        'Convert JSON to CSV locally in your browser: arrays and objects, nested field flattening, delimiter choice, copy and download with no server upload.',
      useCases: ['API responses', 'Table exports', 'Test data', 'ETL drafts'],
      howTo: [
        'Paste a JSON object or array into the editor.',
        'Choose a CSV delimiter: comma, semicolon, tab or pipe.',
        'Click Convert. Nested fields are flattened into dot/bracket path columns.',
        'Copy the CSV or download the .csv file.',
      ],
      faq: [
        {
          question: 'Is JSON sent to a server?',
          answer:
            'No. JSON parsing, field flattening and CSV generation all run locally in your browser.',
        },
        {
          question: 'What happens to nested objects and arrays?',
          answer:
            'Nested values are flattened into columns such as profile.name or tags[0]. Empty objects and arrays are preserved as {} and [].',
        },
        {
          question: 'Which JSON shape is supported?',
          answer:
            'The root value must be an object or an array. An array of objects becomes multiple rows, and a single object becomes one row. Root strings, numbers and booleans show a clear error.',
        },
      ],
    },
    base64: {
      title: 'Base64',
      shortDescription: 'Encode and decode Base64 strings, including URL-safe and no =.',
      longDescription:
        'Local encoding for short text snippets, tokens and test payloads in multiple Base64 formats.',
      seoTitle: 'Base64 encode decode online — devdeck.ru',
      seoDescription:
        'Encode and decode Base64 strings, URL-safe Base64 and Base64 without padding locally in your browser.',
      useCases: ['Payloads', 'Test tokens', 'URL-safe strings', 'Config snippets'],
      howTo: [
        'Paste text or a Base64 string into the editor.',
        'Choose Encode or Decode and a Base64 format: standard, no =, URL-safe or URL-safe no =.',
        'Click Encode or Decode.',
        'Copy the result or swap input and output.',
      ],
      faq: [
        {
          question: 'Is data sent to a server?',
          answer:
            'No. Encoding and decoding happen locally in the browser. Your text stays on your device.',
        },
        {
          question: 'Does it support Base64 without trailing = signs?',
          answer:
            'Yes. Choose "No =" or "URL-safe no =". When decoding, the tool adds the missing padding locally before processing.',
        },
        {
          question: 'Why does decoding fail?',
          answer:
            'The Base64 string may contain invalid characters or be damaged. Standard Base64 uses A-Z, a-z, 0-9, +, / and optional trailing = signs; URL-safe Base64 uses A-Z, a-z, 0-9, - and _.',
        },
        {
          question: 'What is Auto mode?',
          answer:
            'Auto mode checks the input: if it looks like Base64, Decode and the matching format are selected automatically; otherwise Encode is used.',
        },
        {
          question: 'Does it work with non-English text?',
          answer:
            'Yes. Text is converted to UTF-8 before Base64 encoding. All Unicode languages and symbols are supported.',
        },
      ],
    },
    htmlEntities: {
      title: 'HTML entities',
      shortDescription: 'Escape and decode HTML entities, named and numeric.',
      longDescription:
        'A local HTML entities encoder/decoder for markup, templates, CMS fields and test HTML snippets. Escape unsafe characters or decode named and numeric entities in your browser.',
      seoTitle: 'HTML entities encode decode online — devdeck.ru',
      seoDescription:
        'Encode and decode HTML entities locally in your browser: &amp;, &lt;, &quot;, numeric entities, copy and swap with no server upload.',
      useCases: ['HTML templates', 'CMS snippets', 'XML/markup', 'Escaping'],
      howTo: [
        'Paste text or a string with HTML entities into the editor.',
        'Choose Encode to escape HTML characters or Decode to get plain text back.',
        'Optionally encode non-ASCII characters as numeric entities.',
        'Copy the result or swap input and output.',
      ],
      faq: [
        {
          question: 'Is the text sent to a server?',
          answer:
            'No. HTML entity encoding and decoding runs locally in your browser.',
        },
        {
          question: 'Which entities are supported during decode?',
          answer:
            'Decode uses the browser HTML parser, so it supports named entities such as `&amp;` and `&copy;`, decimal numeric entities such as `&#169;` and hexadecimal numeric entities such as `&#xA9;` where the current browser supports them.',
        },
        {
          question: 'Does Encode turn all Unicode into entities?',
          answer:
            'By default it encodes HTML-sensitive characters: &, <, >, quotes, apostrophe and backtick. You can enable numeric encoding for Cyrillic, emoji and other non-ASCII characters separately.',
        },
      ],
    },
    urlCodec: {
      title: 'URL encode/decode',
      shortDescription: 'Encode query params and decode URL fragments.',
      longDescription: 'Fast work with URLs, query strings and percent-encoding without network requests.',
      seoTitle: 'URL encode decode online — devdeck.ru',
      seoDescription:
        'Encode and decode URLs, query params and percent-encoded strings locally in your browser. Free, no sign-up and no server upload.',
      useCases: ['Query params', 'Redirect URLs', 'Webhook payloads', 'UTM and callback URLs'],
      howTo: [
        'Choose Encode or Decode.',
        'Choose the target: whole URL, component or query.',
        'Paste the string and run the processor.',
        'Copy the result or swap input and output.',
      ],
      faq: [
        {
          question: 'Is the URL sent to a server?',
          answer:
            'No. Encode and decode run through built-in browser APIs directly on your device.',
        },
        {
          question: 'How is whole URL different from component?',
          answer:
            'Whole URL keeps separators such as : / ? & = and only encodes unsafe parts. Component encodes the entire string, so it is the right choice for a query parameter value.',
        },
        {
          question: 'Why can decode show an error?',
          answer:
            'Percent-encoding must contain pairs such as %2F. If the input has a lone % or a broken UTF-8 sequence, the browser cannot decode it safely.',
        },
      ],
    },
    utmGenerator: {
      title: 'UTM generator',
      shortDescription: 'Build a UTM-tagged URL while preserving query parameters.',
      longDescription:
        'A local campaign URL builder that replaces existing UTM tags while keeping other parameters and the hash.',
      seoTitle: 'UTM generator online — build UTM links | devdeck.ru',
      seoDescription:
        'UTM generator: build a URL with utm_source, utm_medium and utm_campaign online. Existing query parameters and hashes stay intact, with local browser processing.',
      useCases: ['Email campaigns', 'Ad campaigns', 'Banners', 'Published links'],
      howTo: [
        'Paste the full page address including http:// or https://.',
        'Fill in the required source, medium and campaign fields.',
        'Optionally add term and content.',
        'Build the URL, check it and copy the result.',
      ],
      faq: [
        {
          question: 'Are the URL and UTM values sent to a server?',
          answer:
            'No. The browser URL API builds the result locally, and field values are not sent to a server or analytics.',
        },
        {
          question: 'What happens to the original query and hash?',
          answer:
            'Regular query parameters and the hash are preserved. Existing lowercase UTM parameters are removed and replaced with the new values.',
        },
        {
          question: 'Which fields are required?',
          answer:
            'utm_source, utm_medium and utm_campaign are required. utm_term and utm_content are added only when provided.',
        },
      ],
    },
    uuidGenerator: {
      title: 'UUID generator',
      shortDescription: 'Generate UUID v1, v4, v6 and v7 — one or many, with options.',
      longDescription: 'UUID v1, v4, v6 and v7 generator for identifiers, test data and API requests. Runs locally in your browser using crypto.getRandomValues.',
      seoTitle: 'UUID generator online — devdeck.ru',
      seoDescription: 'Generate UUID v1, v4, v6 and v7 locally in your browser: bulk UUIDs, uppercase/lowercase, with or without hyphens.',
      useCases: ['DB IDs', 'Test data', 'API requests', 'Identifiers'],
      howTo: [
        'Choose the UUID version: v4, v7, v1 or v6.',
        'Set the number of UUIDs (1 to 1000).',
        'Choose case: abc (lowercase) or ABC (uppercase).',
        'Choose hyphen format: with or without hyphens.',
        'Click Generate and copy the result.',
      ],
      faq: [
        {
          question: 'Are UUIDs generated locally?',
          answer: 'Yes. All UUIDs are created in your browser using crypto.getRandomValues. Data never leaves your device.',
        },
        {
          question: 'Can I generate many UUIDs at once?',
          answer: 'Yes. You can generate up to 1000 UUIDs at a time. For larger volumes, use dedicated tools.',
        },
        {
          question: 'How is UUID v4 different from other versions?',
          answer: 'UUID v4 is based on random numbers and suits most use cases. UUID v7 and v6 are time-sortable, which is useful for indexes and logs. UUID v1 is also time-based, but this generator uses a random node id instead of a MAC address.',
        },
        {
          question: 'Why remove hyphens?',
          answer: 'Some systems (e.g. databases, filenames) do not support hyphens in identifiers. Without hyphens the UUID is shorter and easier to copy in certain contexts.',
        },
      ],
    },
    regexTester: {
      title: 'Regex tester',
      shortDescription: 'Test regular expressions, flags, matches and groups.',
      longDescription: 'A local regular expression tester for developers: enter a pattern, flags and test text, highlight matches and inspect groups without sending data to a server.',
      seoTitle: 'Regex tester online — devdeck.ru',
      seoDescription: 'Test regular expressions online: pattern, flags, match highlighting, capture groups and regex errors. Runs locally in your browser.',
      useCases: ['Code regex', 'Log parsing', 'Capture groups', 'Pattern validation'],
      howTo: [
        'Enter a regular expression without wrapping slashes.',
        'Set JavaScript RegExp flags such as g, i, m or u.',
        'Paste test text and click Test.',
        'Review highlighted matches and the group list.',
      ],
      faq: [
        {
          question: 'Are the pattern and test text sent to a server?',
          answer: 'No. Testing runs through JavaScript RegExp in your browser. Data is not sent to or stored on a server.',
        },
        {
          question: 'Which flags are supported?',
          answer: 'The tool supports flags understood by the current browser, such as g, i, m, s, u, v and d. Invalid or duplicate flags show an error.',
        },
        {
          question: 'Why is there a 500 match limit?',
          answer: 'The limit keeps the page responsive for heavy expressions and very large result sets. The first 500 matches are still shown.',
        },
      ],
    },
    jwtDecoder: {
      title: 'JWT decoder',
      shortDescription: 'Inspect JWT header and payload without sending the token to a server.',
      longDescription:
        'Local inspection of JWT JSON content for authentication debugging. The token signature is not verified.',
      seoTitle: 'JWT decoder online — inspect a token locally | devdeck.ru',
      seoDescription:
        'Decode JWT header and payload locally in your browser. The token is never sent to a server and its signature is not verified.',
      useCases: ['Authentication debugging', 'Claims inspection', 'Expiry checks', 'Header inspection'],
      howTo: [
        'Paste a JWT with three dot-separated segments.',
        'Click Decode JWT.',
        'Inspect the formatted header and payload JSON.',
        'Do not treat the result as proof of authenticity: this tool does not verify signatures.',
      ],
      faq: [
        {
          question: 'Is the JWT sent to a server?',
          answer: 'No. The token is decoded only in your browser and its contents are not included in analytics.',
        },
        {
          question: 'Does this tool verify the JWT signature?',
          answer: 'No. It only decodes the header and payload. Claims are trustworthy only after separate signature verification.',
        },
        {
          question: 'Why can\'t the token be decoded?',
          answer: 'A JWT must have three segments. Header and payload must be Base64URL-encoded JSON objects, and a signed JWT needs a non-empty Base64URL signature. With alg "none", the signature must be empty.',
        },
      ],
    },
    cronGenerator: {
      title: 'Cron generator',
      shortDescription: 'Parse cron expressions, build simple schedules and preview next runs.',
      longDescription: 'A local cron helper: validate a 5-field expression, build schedules by minute, hour, day, month and weekday, and preview upcoming runs in your current timezone.',
      seoTitle: 'Cron expression online — generator and checker | devdeck.ru',
      seoDescription: 'Build and check a cron expression online: plain-language schedule, minute, hour, day and month builder, and next runs. Processed locally in your browser.',
      useCases: ['CI schedules', 'Kubernetes CronJob', 'Backups', 'Recurring jobs'],
      howTo: [
        'Paste a 5-field cron expression: minute hour day month weekday.',
        'Click Parse to see the description and upcoming runs.',
        'Use the builder when you need to assemble a simple schedule quickly.',
        'Check the timezone caveat before moving the expression to a server runtime.',
      ],
      faq: [
        {
          question: 'Is the cron expression sent to a server?',
          answer: 'No. Parsing, generation and next-run calculation happen locally in your browser.',
        },
        {
          question: 'Which cron format is supported?',
          answer: 'The tool supports the standard 5-field format: minute hour day month weekday. Seconds and year are not used.',
        },
        {
          question: 'Are MON or JAN supported?',
          answer: 'Yes. Month and weekday fields accept numeric values and short English aliases such as JAN, MON and FRI.',
        },
      ],
    },
    markdownEditor: {
      title: 'Online Markdown editor',
      shortDescription: 'Edit Markdown with an instant, sanitized HTML preview.',
      longDescription:
        'A local Markdown editor with live preview, GFM tables, task lists, document stats and Markdown or HTML export.',
      seoTitle: 'Markdown online — editor with live preview | devdeck.ru',
      seoDescription:
        'Edit Markdown online with a sanitized live preview, copy HTML and download .md or .html. CommonMark and GFM, processed locally in your browser.',
      useCases: ['READMEs and docs', 'Markdown preview', 'Markdown to HTML', 'GFM tables'],
      howTo: [
        'Type or paste Markdown into the left pane.',
        'Check the live preview and word, heading, link and table stats.',
        'Copy the original Markdown or sanitized HTML fragment, or download .md/.html.',
      ],
      faq: [
        {
          question: 'Which Markdown flavor is supported?',
          answer: 'The editor follows CommonMark with GFM-style tables, task lists, strikethrough and autolinks. It does not guarantee pixel-identical rendering on a specific platform.',
        },
        {
          question: 'Can I include raw HTML?',
          answer: 'Raw HTML is detected but never interpreted or included in preview and exports. The result also passes through a sanitizer that removes dangerous elements, attributes and URL schemes.',
        },
        {
          question: 'Is my Markdown sent to a server?',
          answer: 'No. Parsing, preview, stats and exports all run locally in your browser. Images are intentionally never loaded or included in HTML preview/export.',
        },
        {
          question: 'What is the maximum document size?',
          answer: 'The editor accepts up to 256 KiB of UTF-8 Markdown for copy and export. Live preview processes documents up to 64 KiB in a separate Web Worker to bound background parse/render memory and runtime.',
        },
      ],
    },
    textHashGenerator: {
      title: 'Text hash generator',
      shortDescription: 'Generate SHA-256, SHA-1 and MD5 for UTF-8 text in your browser.',
      longDescription:
        'A local hash generator for strings, test payloads and compatibility checks. Your source text never leaves the browser.',
      seoTitle: 'MD5 and SHA text hash generator online | devdeck.ru',
      seoDescription:
        'Generate SHA-256, SHA-1 and MD5 for UTF-8 text locally in your browser. Copy results without file uploads or sending text to a server.',
      useCases: ['Text SHA-256', 'Integration checks', 'Test payloads', 'Legacy MD5 and SHA-1'],
      howTo: [
        'Type or paste text into the left pane.',
        'Click Generate hashes. An empty string has valid hashes too.',
        'Copy the required SHA-256, SHA-1 or MD5 with its own button.',
        'Do not use MD5 or SHA-1 for passwords, signatures or other security-sensitive tasks.',
      ],
      faq: [
        {
          question: 'Is the text sent to a server or analytics?',
          answer: 'No. UTF-8 bytes and hashes are calculated locally in your browser. Analytics receives only operational metadata: the action, algorithm count, text length on invoke and success, successful calculation duration, or an error code — never the text or digest.',
        },
        {
          question: 'Which text encoding is used?',
          answer: 'The text is converted to UTF-8 with the standard TextEncoder. The result matches CLI tools when they receive the same UTF-8 bytes.',
        },
        {
          question: 'Can I use MD5 or SHA-1 for passwords and signatures?',
          answer: 'No. MD5 and SHA-1 are cryptographically broken legacy algorithms included only for compatibility. Password storage requires a dedicated salted password-hashing algorithm.',
        },
      ],
    },
  },

  base64: {
    sourceText: 'Source text',
    base64String: 'Base64 string',
    decodedText: 'Decoded text',
    resultTitle: 'Base64 result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    swap: 'Swap',
    inputEncodePlaceholder: 'Enter text to encode in Base64...',
    inputDecodePlaceholder: 'Enter a Base64 string to decode...',
    outputPlaceholder: 'The result will appear here...',
    encodeLabel: 'Text to encode',
    decodeLabel: 'Base64 string to decode',
    resultLabel: 'Result',
    mode: 'Mode:',
    format: 'Format:',
    variants: {
      standard: 'Standard',
      'standard-no-padding': 'No =',
      'url-safe': 'URL-safe',
      'url-safe-no-padding': 'URL-safe no =',
    },
    encode: 'Encode',
    decode: 'Decode',
    auto: 'Auto',
    autoHint: 'Looks like Base64 — try Decode',
    inputLength: 'Input length',
    resultLength: 'Result length',
    sourceSize: 'Source size',
    characters: 'characters',
    lines: 'lines',
    base64Chars: 'Base64 chars',
    howTitle: 'How it works',
    how:
      'Base64 encodes binary data into text. The standard alphabet uses A-Z, a-z, 0-9, + and /, while the URL-safe variant replaces + and / with - and _. Trailing padding can be kept or removed. Everything runs locally in your browser — text is not sent to a server.',
    whenTitle: 'When to use it',
    when:
      'Encode: preparing text payloads for APIs, passing binary data through JSON, creating Data URLs or storing small files as text. Decode: inspecting Base64 tokens, debugging API responses and decoding JWT segments.',
    errors: {
      encode: 'Encoding error: the text contains unsupported characters',
      emptyDecode: 'No data to decode',
      invalid:
        'Invalid Base64 string for the selected format. Check the alphabet, string length and trailing = signs.',
      invalidUtf8:
        'The decoded result is not valid UTF-8. It may be binary data, not text.',
      unknown: 'Unknown error',
    },
  },

  htmlEntities: {
    sourceText: 'Source text',
    entitiesText: 'HTML entities',
    resultTitle: 'Result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    swap: 'Swap',
    inputEncodePlaceholder: '<button title="Save & close">Save</button>',
    inputDecodePlaceholder: '&lt;button title=&quot;Save &amp; close&quot;&gt;Save&lt;/button&gt;',
    outputPlaceholder: 'The result will appear here...',
    encodeLabel: 'Text to encode as HTML entities',
    decodeLabel: 'HTML entities to decode',
    resultLabel: 'HTML entities result',
    mode: 'Mode:',
    encode: 'Encode',
    decode: 'Decode',
    encodeAction: 'Encode',
    decodeAction: 'Decode',
    encodeNonAscii: 'Encode non-ASCII as numeric entities',
    characters: 'Characters',
    lines: 'Lines',
    entities: 'Entities',
    inputEntities: 'Input entities',
    resultEntities: 'Result entities',
    detectedEntities: 'matched as &...;',
    processing: 'Processing',
    localOnly: 'Local in browser',
    howTitle: 'How it works',
    how:
      'Encode replaces HTML-significant characters &, <, >, quotes, apostrophe and backtick with entities so text can be inserted into markup safely. Decode uses the browser HTML parser through a textarea, so it understands named entities such as `&amp;`, decimal numeric such as `&#169;` and hexadecimal numeric such as `&#xA9;`.',
    whenTitle: 'When to use it',
    when:
      'Use it for HTML templates, CMS snippets, XML/markup fragments, email markup and test payloads when you need to quickly inspect the escaped or decoded version of a string.',
    errors: {
      unknown: 'Could not process HTML entities.',
    },
  },

  urlCodec: {
    sourceText: 'Source string',
    encodedText: 'Encoded URL string',
    resultTitle: 'Result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    swap: 'Swap',
    inputEncodePlaceholder: 'https://example.dev/search?q=hello world&redirect=/docs',
    inputDecodePlaceholder: 'hello%20world%2Fdocs%3Ftab%3Dapi',
    outputPlaceholder: 'The result will appear here...',
    encodeLabel: 'String to URL encode',
    decodeLabel: 'Percent-encoded string to decode',
    resultLabel: 'URL encode/decode result',
    mode: 'Mode:',
    target: 'Target:',
    targets: {
      url: 'Whole URL',
      component: 'Component',
      query: 'Query',
    },
    encode: 'Encode',
    decode: 'Decode',
    encodeAction: 'Encode',
    decodeAction: 'Decode',
    characters: 'Characters',
    lines: 'Lines',
    percentSequences: '% sequences',
    queryParts: 'Query parts',
    inResult: 'in result',
    simpleQueryHint: 'simple & split',
    processing: 'Processing',
    localOnly: 'Local in browser',
    howTitle: 'How it works',
    how:
      'Whole URL uses encodeURI/decodeURI and keeps URL separators. Component uses encodeURIComponent/decodeURIComponent and encodes the full string. Query works like component, but encode turns spaces into + and decode treats + as a space.',
    whenTitle: 'When to use it',
    when:
      'Use it for query params, callback URLs, redirect_uri values, webhook payloads, UTM strings and debugging percent-encoded values in logs or API requests.',
    errors: {
      invalidPercent:
        'Invalid percent-encoding. Check lone % characters, pairs such as %2F and UTF-8 sequences.',
      invalidEncoding:
        'Could not encode the string. Check the input for invalid Unicode surrogate characters.',
      unknown: 'Could not process the URL string.',
    },
  },

  utmGenerator: {
    formTitle: 'Campaign parameters',
    baseUrl: 'Base URL',
    source: 'utm_source',
    medium: 'utm_medium',
    campaign: 'utm_campaign',
    term: 'utm_term (optional)',
    content: 'utm_content (optional)',
    optionalPlaceholder: 'Leave blank when not needed',
    build: 'Build URL',
    reset: 'Reset',
    previewTitle: 'Result URL',
    previewLabel: 'URL with UTM parameters',
    previewPlaceholder: 'Fill in the fields and build the URL.',
    copy: 'Copy',
    copied: 'Copied',
    localOnly:
      'The URL is built locally. The tool does not open it or send field values to a server.',
    errors: {
      'empty-url': 'Enter the base URL.',
      'invalid-url': 'Enter a valid full URL such as https://example.com/page.',
      'unsupported-protocol': 'Only the safe HTTP and HTTPS protocols are supported.',
      'missing-required-fields': 'Fill in utm_source, utm_medium and utm_campaign.',
    },
  },

  jsonFormatter: {
    input: 'Input JSON',
    output: 'Result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    format: 'Format',
    minify: 'Minify',
    validate: 'Validate',
    inputPlaceholder: 'Paste JSON to format...',
    outputPlaceholder: 'Formatted JSON will appear here...',
    valid: 'JSON is valid ✓',
    characters: 'characters',
    lines: 'lines',
    errors: {
      invalidJson: 'Invalid JSON: {error}',
    },
    howTitle: 'How it works',
    how:
      'The JSON formatter parses your JSON using the browser\'s native JSON.parse, checks syntax and formats the output with indentation. Minification removes extra whitespace while preserving structure. All processing happens locally in your browser — data is never sent to a server.',
    whenTitle: 'When to use it',
    when:
      'Format: preparing API responses for reading, debugging logs, inspecting config files. Minify: reducing JSON size for network transfer, preparing request payloads. Validate: quickly checking JSON before using it in code or configuration.',
  },

  jsonMinifier: {
    input: 'Input JSON',
    output: 'Minified JSON',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    minify: 'Minify',
    inputPlaceholder:
      '{\n  "service": "api",\n  "enabled": true,\n  "limits": { "retries": 3 }\n}',
    outputPlaceholder: 'Compact JSON will appear here...',
    minified: 'JSON minified',
    characters: 'characters',
    lines: 'lines',
    saved: 'saved',
    errors: {
      invalidJson: 'Invalid JSON: {error}',
    },
    howTitle: 'How it works',
    how:
      'The JSON minifier uses the same local JSON.parse parser as JSON formatter. After successful validation it serializes the value with JSON.stringify and no indentation, so whitespace and line breaks disappear while the data structure stays intact.',
    whenTitle: 'When to use it',
    when:
      'Minification is useful for compact API payloads, webhook bodies, JSON in environment variables, test fixtures and configs where size or one-line pasting matters. Use JSON formatter for reading and review.',
  },

  yamlFormatter: {
    input: 'Input YAML',
    output: 'Result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    format: 'Format',
    validate: 'Validate',
    inputPlaceholder: 'Paste YAML to format...',
    outputPlaceholder: 'Formatted YAML will appear here...',
    valid: 'YAML is valid',
    characters: 'characters',
    lines: 'lines',
    errors: {
      invalidYaml: 'Invalid YAML: {error}',
    },
    howTitle: 'How it works',
    how:
      'The YAML formatter parses the document in your browser, checks parser errors and prints YAML with consistent indentation. For YAML streams, multiple documents are formatted in sequence. All processing is local — data is never sent to a server.',
    whenTitle: 'When to use it',
    when:
      'Formatting helps with config files, CI/CD pipelines, Kubernetes manifests, docker compose snippets and documentation. Validation helps catch syntax errors before committing YAML or pasting it into another tool.',
  },

  htmlFormatter: {
    input: 'Input HTML',
    output: 'Result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    format: 'Format',
    minify: 'Minify',
    inputPlaceholder: '<main>\n  <h1>Title</h1>\n  <p>HTML snippet...</p>\n</main>',
    outputPlaceholder: 'Formatted or minified HTML will appear here...',
    formatted: 'HTML formatted',
    minified: 'HTML minified',
    characters: 'characters',
    lines: 'lines',
    errors: {
      invalidHtml: 'Could not process HTML: {error}',
    },
    howTitle: 'How it works',
    how:
      'The HTML formatter uses the browser build of the Prettier HTML parser. Formatting normalizes indentation and line breaks, while minification first checks the markup with the parser and then compresses whitespace between tags. Everything runs locally — HTML is never sent to a server.',
    whenTitle: 'When to use it',
    when:
      'Formatting helps inspect template snippets, email markup, copied HTML and components before review. Minification is useful for compactly pasting small HTML fragments into configs, tests or CMS fields.',
  },

  javascriptFormatter: {
    input: 'Input JavaScript',
    output: 'Result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    format: 'Format',
    minify: 'Minify',
    inputPlaceholder: 'function greet(name){ console.log(`Hello, ${name}`) }',
    outputPlaceholder: 'Formatted or minified JavaScript will appear here...',
    formatted: 'JavaScript formatted',
    minified: 'JavaScript minified',
    characters: 'characters',
    lines: 'lines',
    errors: {
      invalidJavaScript: 'Could not process JavaScript: {error}',
    },
    howTitle: 'How it works',
    how:
      'The JavaScript formatter lazy-loads Prettier standalone with Babel/ESTree parser plugins for formatting and Terser for minification. Both steps work with the AST locally in your browser and do not execute user code.',
    whenTitle: 'When to use it',
    when:
      'Formatting helps read snippets, debug copied code and prepare fragments for review. Minification is useful when you need a compact version of a small browser-side script or test payload.',
  },

  cssFormatter: {
    input: 'Input CSS',
    output: 'Result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    format: 'Format',
    minify: 'Minify',
    inputPlaceholder: '.card { display: grid; gap: 16px; color: #1f2937; }',
    outputPlaceholder: 'Formatted or minified CSS will appear here...',
    formatted: 'CSS formatted',
    minified: 'CSS minified',
    characters: 'characters',
    lines: 'lines',
    errors: {
      invalidCss: 'Could not process CSS: {error}',
    },
    howTitle: 'How it works',
    how:
      'The CSS formatter lazy-loads Prettier standalone with the PostCSS parser plugin to parse and format stylesheets. Minification validates CSS with the same parser first, then removes comments and extra whitespace. Everything runs locally — CSS is never sent to a server.',
    whenTitle: 'When to use it',
    when:
      'Formatting helps inspect copied stylesheet fragments, component styles, theme tokens and CSS from DevTools before review. Minification is useful for compactly pasting small styles into tests, embeds or config drafts.',
  },

  sqlFormatter: {
    input: 'Input SQL',
    output: 'Result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    format: 'Format',
    formatting: 'Formatting...',
    dialect: 'Dialect',
    inputPlaceholder: 'select u.id, u.email, count(o.id) as orders from users u left join orders o on o.user_id = u.id where u.active = true group by u.id, u.email order by orders desc;',
    outputPlaceholder: 'Formatted SQL will appear here...',
    formatted: 'SQL formatted',
    characters: 'characters',
    lines: 'lines',
    dialects: {
      sql: 'Generic SQL',
      postgresql: 'PostgreSQL',
      mysql: 'MySQL',
      sqlite: 'SQLite',
      mariadb: 'MariaDB',
      transactsql: 'Transact-SQL',
      bigquery: 'BigQuery',
      snowflake: 'Snowflake',
    },
    errors: {
      invalidSql: 'Could not process SQL: {error}',
    },
    howTitle: 'How it works',
    how:
      'The SQL formatter loads a local browser formatter library and rewrites the query with consistent indentation, line breaks and uppercase keywords. The selected dialect helps the parser with common syntax differences, but it does not perform semantic validation or connect to a database.',
    whenTitle: 'When to use it',
    when:
      'Formatting helps before code review, while reading long SELECT statements with JOINs or CTEs, preparing migrations and inspecting SQL from logs. Use your database or CI for execution plans, column types and table existence checks.',
  },

  xmlFormatter: {
    input: 'Input XML',
    output: 'Result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    format: 'Format',
    minify: 'Minify',
    validate: 'Validate',
    inputPlaceholder: '<root>\n  <item id="1">value</item>\n</root>',
    outputPlaceholder: 'Formatted or minified XML will appear here...',
    formatted: 'XML formatted',
    minified: 'XML minified',
    valid: 'XML is valid',
    characters: 'characters',
    lines: 'lines',
    errors: {
      invalidXml: 'Invalid XML: {error}',
    },
    howTitle: 'How it works',
    how:
      'The XML formatter first validates the document with the browser DOMParser, then formats the structure with two-space indentation or removes whitespace between tags for minification. Everything runs locally — XML is never sent to a server.',
    whenTitle: 'When to use it',
    when:
      'Use it for SOAP/XML responses, RSS, sitemap.xml, Android/vector XML, configs and test payloads when you need to quickly check well-formed XML before pasting it into code or sending a request.',
  },

  csvToJson: {
    input: 'Input CSV',
    output: 'JSON result',
    upload: 'Upload CSV',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    download: 'Download .json',
    convert: 'Convert',
    convertLarge: 'Convert large input',
    autoPreviewLimit:
      'Auto-preview runs up to 128 KiB. Convert larger input explicitly.',
    inputPlaceholder: 'name,email\nAda,ada@example.com\nLinus,linus@example.com',
    outputPlaceholder: 'JSON will appear here after conversion...',
    delimiter: 'Delimiter',
    headerRow: 'First row is headers',
    detected: 'Detected',
    delimiterUsed: 'Delimiter',
    rows: 'Data rows',
    columns: 'Columns',
    characters: 'characters',
    lines: 'lines',
    delimiters: {
      auto: 'Auto',
      comma: 'Comma (,)',
      semicolon: 'Semicolon (;)',
      tab: 'Tab',
      pipe: 'Pipe (|)',
    },
    delimiterSymbols: {
      ',': ',',
      ';': ';',
      '\t': 'Tab',
      '|': '|',
    },
    errors: {
      empty: 'Add CSV to convert.',
      unclosedQuote: 'Invalid CSV: a quoted value is not closed.',
      fileTooLarge: 'The file is too large. Upload a CSV up to 1 MB.',
      fileRead: 'Could not read the file.',
    },
    howTitle: 'How it works',
    how:
      'The tool parses CSV locally in your browser: detects the delimiter, respects quoted values and converts rows into objects when header row mode is enabled. With headers disabled, the result is an array of arrays.',
    whenTitle: 'When to use it',
    when:
      'CSV to JSON helps with quick API payloads, checking table exports, preparing test data, prototyping ETL and manually normalizing small exports.',
  },

  jsonToCsv: {
    input: 'Input JSON',
    output: 'CSV result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    download: 'Download .csv',
    convert: 'Convert',
    convertLarge: 'Convert large input',
    autoPreviewLimit:
      'Auto-preview runs up to 128 KiB. Convert larger input explicitly.',
    inputPlaceholder:
      '[\n  { "name": "Ada", "email": "ada@example.com", "profile": { "role": "admin" } },\n  { "name": "Linus", "email": "linus@example.com", "profile": { "role": "dev" } }\n]',
    outputPlaceholder: 'CSV will appear here after conversion...',
    delimiter: 'Delimiter',
    rows: 'Data rows',
    columns: 'Columns',
    flattened: 'Flattened fields',
    characters: 'characters',
    lines: 'lines',
    delimiters: {
      comma: 'Comma (,)',
      semicolon: 'Semicolon (;)',
      tab: 'Tab',
      pipe: 'Pipe (|)',
    },
    errors: {
      empty: 'Add JSON to convert.',
      invalidJson: 'Invalid JSON: {error}',
      unsupportedRoot:
        'The root value must be an object or array. Strings, numbers and booleans cannot become a table without columns.',
      complexityLimit:
        'JSON is too deeply nested or contains too many rows, columns, or values for a safe local preview.',
      processingFailed:
        'JSON conversion failed. Check the structure and try a smaller document.',
      unknown: 'unknown error',
    },
    howTitle: 'How it works',
    how:
      'The tool parses JSON with JSON.parse in your browser and builds CSV with headers. A root object becomes one row, and an array becomes multiple rows. Nested objects and arrays are flattened into path columns such as profile.name and tags[0]; values are escaped with standard CSV rules.',
    whenTitle: 'When to use it',
    when:
      'JSON to CSV helps when you need to move an API response, mock data, webhook payload or small ETL draft into a table, BI tool or spreadsheet.',
  },

  uuidGenerator: {
    controls: 'Generation settings',
    version: 'Version',
    count: 'Count',
    case: 'Case',
    hyphens: 'Hyphens',
    withHyphens: 'With hyphens',
    withoutHyphens: 'Without hyphens',
    generate: 'Generate',
    results: 'Results',
    copy: 'Copy',
    copyAll: 'Copy all',
    copied: 'Copied',
    noResults: 'Click Generate to get UUIDs.',
    uppercase: 'Uppercase',
    lowercase: 'Lowercase',
    versionDescriptions: {
      v1: 'Time + random node',
      v4: 'Random UUIDs',
      v6: 'Time, sortable layout',
      v7: 'Unix time + random',
    },
  },

  regexTester: {
    patternTitle: 'Regular expression',
    pattern: 'Pattern',
    flags: 'Flags',
    patternPlaceholder: 'Example: order-(\\d+)',
    flagsPlaceholder: 'gim',
    run: 'Test',
    clear: 'Clear',
    testText: 'Test text',
    textPlaceholder: 'Paste text to test...',
    highlightedText: 'Highlighted matches',
    statsTitle: 'Result',
    matches: 'Matches',
    groups: 'Groups',
    groupsTitle: 'Match groups',
    match: 'Match',
    noText: 'Text will appear here after input.',
    noMatches: 'No matches yet.',
    noGroups: 'This match has no groups.',
    fixRegex: 'Fix the regular expression to see groups.',
    emptyMatch: '(empty match)',
    emptyGroup: '(empty)',
    copySummary: 'Copy result',
    copyShare: 'Copy link',
    copied: 'Copied',
    truncated: 'Showing the first {limit} matches to keep the page responsive.',
    errors: {
      emptyPattern: 'Enter a regular expression.',
      invalid: 'Invalid regular expression: {error}',
    },
  },

  jwtDecoder: {
    token: 'JWT token',
    result: 'Header and payload',
    decode: 'Decode JWT',
    clear: 'Clear',
    copy: 'Copy JSON',
    copied: 'Copied',
    inputPlaceholder: 'Paste a JWT: eyJhbGciOiJIUzI1NiIs...',
    outputPlaceholder: 'Decoded header and payload will appear here...',
    inputAriaLabel: 'JWT to decode locally',
    outputAriaLabel: 'Decoded JWT header and payload',
    signatureWarning: 'Important: this tool only decodes the contents. The JWT signature is not verified.',
    errors: {
      'invalid-format': 'Invalid JWT format. Expected header.payload.signature with a non-empty signature. For alg "none", the signature must instead be empty.',
      'invalid-base64url': 'One of the JWT segments contains invalid Base64URL data.',
      'invalid-json': 'The decoded header or payload is not a valid UTF-8 JSON object.',
    },
  },

  markdownEditor: {
    inputTitle: 'Markdown',
    previewTitle: 'HTML preview',
    inputPlaceholder: '# Heading\n\nStart writing **Markdown**…',
    inputAriaLabel: 'Markdown for local preview',
    previewPlaceholder: 'The preview will appear as you type Markdown.',
    previewPending: 'Updating preview…',
    copyMarkdown: 'Copy Markdown',
    copyHtml: 'Copy HTML',
    copied: 'Copied',
    downloadMarkdown: 'Download .md',
    downloadHtml: 'Download .html',
    clear: 'Clear',
    words: 'Words',
    headings: 'Headings',
    links: 'Links',
    tables: 'Tables',
    flavorNote: 'CommonMark + GFM: documents up to 256 KiB in UTF-8, preview up to 64 KiB. Other platforms may render it differently.',
    rawHtmlWarning: 'Raw HTML was detected and intentionally excluded from the preview and HTML export.',
    imageWarning: 'Images are not loaded and are excluded from preview and HTML export so local processing cannot trigger network requests.',
    sourceTooLarge: 'Markdown exceeds the overall {limit} KiB UTF-8 limit. The change was rejected; the previous accepted source and exports remain available.',
    previewTooLarge: 'The source was accepted but exceeds the {limit} KiB UTF-8 preview limit. You can copy or download the .md; reduce it for HTML preview/export.',
    renderError: 'The preview could not be rendered. Your Markdown is intact; try changing the problematic fragment.',
    exportTitle: 'Markdown document',
  },

  hashGenerator: {
    inputTitle: 'Source text',
    outputTitle: 'Hashes',
    generate: 'Generate hashes',
    generating: 'Generating…',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    legacy: 'Legacy',
    characters: 'UTF-16 characters',
    inputPlaceholder: 'Enter text to hash…',
    inputAriaLabel: 'Text to hash locally',
    outputPlaceholder: 'Click Generate hashes',
    utf8Note: 'Text is encoded as UTF-8 before hashing. Neither the text nor the resulting hashes are sent to a server or analytics.',
    securityWarning: 'MD5 and SHA-1 are included for compatibility only. Do not use them for passwords, digital signatures or other security-sensitive tasks.',
    error: 'Hashes could not be generated in this browser. Check Web Crypto support and try again.',
  },

  cronGenerator: {
    expressionTitle: 'Cron expression',
    expressionLabel: '5 fields: minute hour day month weekday',
    parse: 'Parse',
    reset: 'Reset',
    copy: 'Copy',
    copied: 'Copied',
    builderTitle: 'Build schedule',
    applyBuilder: 'Apply',
    humanTitle: 'Schedule description',
    nextRunsTitle: 'Next runs',
    fixExpression: 'Fix the cron expression to see the schedule.',
    noRuns: 'Upcoming runs will appear after a valid expression.',
    timezoneCaveat:
      'Examples are calculated in your browser timezone. A server, CI runner or Kubernetes CronJob may use a different timezone.',
    daySemantics:
      'When day and weekday are both restricted, a run is considered due when either field matches.',
    stepPlaceholder: 'Step, e.g. 15',
    rangeStart: 'From',
    rangeEnd: 'To',
    fields: {
      minute: 'Minute',
      hour: 'Hour',
      day: 'Day of month',
      month: 'Month',
      weekday: 'Weekday',
    },
    builderModes: {
      any: 'Any',
      specific: 'Specific value',
      every: 'Every N',
      range: 'Range',
    },
    builderPlaceholders: {
      minute: '0, 15 or 0,30',
      hour: '9 or 9,18',
      day: '1 or 1,15',
      month: '1 or JAN',
      weekday: '1 or MON',
    },
    summary: {
      any: 'Any {field}',
      every: 'Every {step} in {field}',
      range: '{field}: range {values}',
      specific: '{field}: {values}',
    },
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    errors: {
      fieldCount: 'Expected 5 cron fields, got {count}.',
      emptyField: 'Field must not be empty.',
      emptyPart: 'Field part must not be empty.',
      invalidValue: 'Invalid value "{value}".',
      outOfRange: 'Value "{value}" is outside {min}-{max}.',
      invalidStep: 'Invalid step "{step}".',
      invalidRange: 'Invalid range "{range}".',
      noRuns: 'Could not find upcoming runs within 5 years.',
    },
  },

  textDiff: {
    original: 'Original text',
    modified: 'Modified text',
    originalPlaceholder: 'Paste original code, config or text…',
    modifiedPlaceholder: 'Paste the modified version…',
    options: 'Comparison options',
    ignoreCase: 'Ignore case',
    ignoreWhitespace: 'Ignore leading/trailing whitespace',
    limit: 'Up to {size} KB and {lines} lines on each side',
    compare: 'Compare',
    comparing: 'Comparing…',
    swap: 'Swap',
    sample: 'Sample',
    clear: 'Clear',
    result: 'Comparison result',
    resultAria: 'Line-by-line text comparison result',
    summary: 'Added lines: {added}, removed: {removed}',
    identical: 'The texts match with the selected options.',
    copy: 'Copy diff',
    copied: 'Copied',
    collapsed: '{count} unchanged lines hidden',
    errors: {
      bytes: 'One text exceeds the 500 KB limit. Shorten it and try again.',
      lines: 'One text exceeds the 5,000-line limit. Shorten it and try again.',
      timeout: 'Comparison took too long. Shorten the texts or compare more similar fragments.',
      failed: 'Could not compare the texts. Check the input and try again.',
    },
  },

  blog: {
    breadcrumb: 'Blog',
    title: 'devdeck blog',
    description:
      'Practical notes about JSON, YAML, Base64, UUIDs, regular expressions and local developer tools.',
    languageAria: 'Article language',
    postLanguageAria: 'Article language',
    empty: 'No posts in this language yet.',
    notFoundTitle: 'Post not found',
    notFoundDescription: 'This devdeck blog post does not exist or the link is outdated.',
    relatedTools: 'Related tools',
    localeName: {
      ru: 'Русский',
      en: 'English',
    },
  },

  seo: {
    indexTitle: 'Free online developer tools — devdeck.ru',
    indexDescription:
      'Free local developer tools: JSON formatter, YAML formatter, XML formatter, Base64, HTML entities, UUID generator, cron generator and regex tester.',
    blogTitle: 'devdeck blog - notes about developer tools',
    blogDescription:
      'Practical notes about JSON, YAML, Base64, UUIDs, regex and local developer payload processing in the browser.',
  },
}
